import { useState } from 'react';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { createBreadcrumbSchema, createContactPageSchema } from '../lib/schema';
import { pagesData } from '../data';
import { Phone, Mail, MapPin, Send, CheckCircle2, Shield, ShieldCheck } from 'lucide-react';
import { getOptimizedImageUrl } from '../lib/utils';

export default function Contact() {
  const page = pagesData.kontakt;
  const [status, setStatus] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  if (!page) return <div className="py-40 text-center">Siden blev ikke fundet.</div>;

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'Forside', url: 'https://janhedegaard.dk/' },
    { name: 'Kontakt', url: 'https://janhedegaard.dk/kontakt' }
  ]);

  const contactPageSchema = createContactPageSchema();
  const schemaList = [contactPageSchema, breadcrumbSchema];

  return (
    <>
      <SEO 
        title={page.title} 
        description={page.meta?.description || page.meta?.ogDescription}
        image={page.meta?.ogImage}
        schemaMarkup={schemaList}
      />

      {/* Hero Section */}
      <section className="relative min-h-[480px] py-24 md:py-28 flex items-center bg-stone-900">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/6015610c-faa3-4021-bc7e-a866f30ca512/cfedd232-70ee-41da-a16f-a4d21d9510cb.jpg", 1600)} 
            alt="Gravemaskine i arbejde ved udgravning til kloak og fundament" 
            className="w-full h-full object-cover opacity-55"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950/70 via-stone-900/40 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full mt-16 pt-[40px]">
          <div className="mb-6">
            <Breadcrumbs items={[{ label: 'Kontakt', href: '/kontakt' }]} isDark={true} />
          </div>
          <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-400 px-4 py-1.5 rounded-full text-sm font-semibold border border-orange-500/30 mb-6">
            <ShieldCheck className="w-4 h-4 text-orange-500" />
            <span>Autoriseret Kloakmester</span>
          </div>
          <h1 className="text-[1.8rem] md:text-6xl font-bold text-white drop-shadow-lg max-w-4xl leading-[1.15]">
            {page.headline}
          </h1>
          <p className="mt-6 text-lg md:text-xl text-stone-300 max-w-2xl drop-shadow">
            {page.subhead}
          </p>
        </div>
      </section>

      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Info */}
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-12">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-stone-200 hover:border-orange-500 transition-colors group">
                  <Phone className="w-6 h-6 text-orange-600 mb-4" />
                  <p className="font-bold text-stone-900 mb-2">Ring til os</p>
                  <a href={`tel:${page.phone.replace(/\s+/g, '')}`} className="text-stone-600 hover:text-orange-600 transition-colors font-medium">
                    {page.phone}
                  </a>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm border border-stone-200 hover:border-orange-500 transition-colors group">
                  <Mail className="w-6 h-6 text-orange-600 mb-4" />
                  <p className="font-bold text-stone-900 mb-2">Send en mail</p>
                  <a href={`mailto:${page.email}`} className="text-stone-600 hover:text-orange-600 transition-colors font-medium">
                    {page.email}
                  </a>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm border border-stone-200 hover:border-orange-500 transition-colors group sm:col-span-2">
                  <MapPin className="w-6 h-6 text-orange-600 mb-4" />
                  <p className="font-bold text-stone-900 mb-2">Besøg os</p>
                  <a 
                    href={page.addressLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-stone-600 hover:text-orange-600 transition-colors font-medium block"
                  >
                    {page.address}
                  </a>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3 bg-orange-50 p-4 rounded-lg border border-orange-200 text-stone-800 text-sm font-semibold">
                  <ShieldCheck className="w-5 h-5 text-orange-600 shrink-0" />
                  <span>Autoriseret Kloakmester</span>
                </div>
                <div className="flex items-center gap-3 bg-stone-100 p-4 rounded-lg border border-stone-200 text-stone-600 text-sm">
                  <Shield className="w-5 h-5 text-orange-600 shrink-0" />
                  <div className="flex flex-col">
                    <span>CVR nr.: {page.cvr}</span>
                    <span className="text-xs text-stone-500 mt-0.5">EAN nr.: {page.ean}</span>
                  </div>
                </div>

                {/* 3byggetilbud Social Proof */}
                <div className="bg-white p-5 rounded-xl border border-stone-200 shadow-xs flex items-start gap-3.5 mt-4">
                  <span className="text-base shrink-0 mt-0.5" role="img" aria-label="Stjerne">⭐</span>
                  <div className="text-stone-700 text-sm leading-relaxed">
                    <p className="font-bold text-stone-900 mb-1">Kundetilfredshed i top</p>
                    <p>
                      Vi vægter tilfredse kunder og ordentligt arbejde højt. Se vores seneste anmeldelser og bedømmelser på vores offentlige profil hos{' '}
                      <a
                        href="https://www.3byggetilbud.dk/leverandoer/entreprenr-jan-hedegaard-aps/"
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="text-orange-600 font-semibold underline hover:text-orange-700 transition-colors"
                      >
                        3byggetilbud.dk
                      </a>
                      , hvor vi har 5 ud af 5 stjerner.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="bg-white p-8 md:p-12 rounded-2xl shadow-xl border border-stone-100">
              {status === 'success' ? (
                <div className="text-center py-12">
                  <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-stone-900 mb-4">Tak for din besked!</h2>
                  <p className="text-stone-600 mb-8">Vi har modtaget din henvendelse og vender tilbage til dig hurtigst muligt.</p>
                  <button 
                    onClick={() => setStatus('idle')}
                    className="text-orange-600 font-bold hover:underline"
                  >
                    Send en ny besked
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <h2 className="text-2xl font-bold text-stone-900 mb-6">{page.formTitle}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-bold text-stone-900 mb-2">Navn</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                        placeholder="Dit navn"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-bold text-stone-900 mb-2">Email</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                        placeholder="din@email.dk"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-bold text-stone-900 mb-2">Telefon</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                      placeholder="Dit telefonnummer"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-bold text-stone-900 mb-2">Besked</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all resize-none"
                      placeholder="Hvordan kan vi hjælpe dig?"
                    />
                  </div>

                  {/* Honeypot */}
                  <div className="sr-only absolute" aria-hidden="true">
                    <label htmlFor="website_url">Please do not fill in this field.</label>
                    <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
                  </div>

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full bg-orange-600 text-white py-4 rounded-lg font-bold text-lg hover:bg-orange-700 transition-all shadow-lg shadow-orange-900/20 disabled:opacity-50 flex items-center justify-center gap-3"
                  >
                    {status === 'submitting' ? 'Sender...' : (
                      <>
                        {page.formButton}
                        <Send className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Medarbejdere / Direkte kontakt */}
      <section className="py-20 bg-white border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900">Direkte kontakt</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Jan Hedegaard */}
            <div className="bg-stone-50 rounded-xl border border-stone-200 shadow-sm flex flex-col justify-between hover:border-orange-500 transition-all duration-300 overflow-hidden">
              <div>
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=400,height=500,fit=cover,quality=85,format=auto/assets/6015610c-faa3-4021-bc7e-a866f30ca512/content-1_1.jpg" 
                  alt="Autoriseret kloakmester og direktør ved firmabil" 
                  className="w-full h-64 object-cover mb-4"
                />
                <div className="px-6">
                  <h3 className="font-bold text-stone-900 text-lg mb-1">Jan Hedegaard</h3>
                  <p className="text-orange-600 text-sm font-semibold mb-4">Direktør, Aut. Kloakmester</p>
                </div>
              </div>
              <div className="px-6 pb-6">
                <div className="space-y-3 pt-4 border-t border-stone-200/60 text-sm">
                  <a href="tel:+4522458567" className="flex items-center gap-2.5 text-stone-600 hover:text-orange-600 transition-colors font-medium">
                    <Phone className="w-4 h-4 text-orange-600 shrink-0" />
                    <span>22 45 85 67</span>
                  </a>
                  <a href="mailto:jan@janhedegaard.dk" className="flex items-center gap-2.5 text-stone-600 hover:text-orange-600 transition-colors font-medium break-all">
                    <Mail className="w-4 h-4 text-orange-600 shrink-0" />
                    <span>jan@janhedegaard.dk</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Louise */}
            <div className="bg-stone-50 rounded-xl border border-stone-200 shadow-sm flex flex-col justify-between hover:border-orange-500 transition-all duration-300 overflow-hidden">
              <div>
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=400,height=500,fit=cover,quality=85,format=auto/assets/6015610c-faa3-4021-bc7e-a866f30ca512/content_1.jpg" 
                  alt="Bogholderi og administration på kontoret" 
                  className="w-full h-64 object-cover mb-4"
                />
                <div className="px-6">
                  <h3 className="font-bold text-stone-900 text-lg mb-1">Louise</h3>
                  <p className="text-orange-600 text-sm font-semibold mb-4">Bogholderi</p>
                </div>
              </div>
              <div className="px-6 pb-6">
                <div className="space-y-3 pt-4 border-t border-stone-200/60 text-sm">
                  <a href="tel:+4552245895" className="flex items-center gap-2.5 text-stone-600 hover:text-orange-600 transition-colors font-medium">
                    <Phone className="w-4 h-4 text-orange-600 shrink-0" />
                    <span>52 24 58 95</span>
                  </a>
                  <a href="mailto:Louise@janhedegaard.dk" className="flex items-center gap-2.5 text-stone-600 hover:text-orange-600 transition-colors font-medium break-all">
                    <Mail className="w-4 h-4 text-orange-600 shrink-0" />
                    <span>Louise@janhedegaard.dk</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Anders */}
            <div className="bg-stone-50 rounded-xl border border-stone-200 shadow-sm flex flex-col justify-between hover:border-orange-500 transition-all duration-300 overflow-hidden">
              <div>
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=400,height=500,fit=cover,quality=85,format=auto/assets/6015610c-faa3-4021-bc7e-a866f30ca512/content-2.jpg" 
                  alt="Anlægsstruktør i arbejdstøj klar til belægnings- og kloakarbejde" 
                  className="w-full h-64 object-cover mb-4"
                />
                <div className="px-6">
                  <h3 className="font-bold text-stone-900 text-lg mb-1">Anders</h3>
                  <p className="text-orange-600 text-sm font-semibold mb-4">Anlægsstruktør</p>
                </div>
              </div>
              <div className="px-6 pb-6">
                <div className="space-y-3 pt-4 border-t border-stone-200/60 text-sm">
                  <a href="tel:+4531253515" className="flex items-center gap-2.5 text-stone-600 hover:text-orange-600 transition-colors font-medium">
                    <Phone className="w-4 h-4 text-orange-600 shrink-0" />
                    <span>31 25 35 15</span>
                  </a>
                  <a href="mailto:Anders@janhedegaard.dk" className="flex items-center gap-2.5 text-stone-600 hover:text-orange-600 transition-colors font-medium break-all">
                    <Mail className="w-4 h-4 text-orange-600 shrink-0" />
                    <span>Anders@janhedegaard.dk</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Skoledreng / Arbejdsdreng */}
            <div className="bg-stone-50 rounded-xl border border-stone-200 shadow-sm flex flex-col justify-between hover:border-orange-500 transition-all duration-300 overflow-hidden">
              <div>
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=400,height=500,fit=cover,quality=85,format=auto/assets/6015610c-faa3-4021-bc7e-a866f30ca512/content-3_1.jpg" 
                  alt="Ungarbejder og arbejdsdreng på byggepladsen" 
                  className="w-full h-64 object-cover mb-4"
                />
                <div className="px-6">
                  <h3 className="font-bold text-stone-900 text-lg mb-1">Skoledreng / Arbejdsdreng</h3>
                  <p className="text-orange-600 text-sm font-semibold mb-4">Ungarbejder</p>
                </div>
              </div>
              <div className="px-6 pb-6">
                <div className="space-y-3 pt-4 border-t border-stone-200/60 text-sm text-stone-500 italic">
                  Søger du fritidsjob eller ungarbejde? Kontakt os gerne på hovednummeret eller mail.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
